// /home/coder/project/frontend/src/pages/machinery/machines/MachConstMachinesList.tsx file

import React, { useState, useMemo, useCallback } from 'react';
import { Spin, Card, Tag, message, Typography, Modal, AutoComplete, Input, Space } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useGetVwConstMachines, useGetCompanies, useGetPersonnel } from '../../../hooks/genericHooks'; // Use the new hook and lookup hooks
import { alterEnumTypes, deleteData, insertData, updateData } from '../../../services/api'; // Import API functions
import { VwConstMachines, ConstMachines, Company, Personnel } from '../../../types'; // Use the view type and base type
import { PlusOutlined, ToolOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import StandardTable, { createActionColumn } from '../../../components/common/StandardTable';
import FilterBar from '../../../components/common/FilterBar';
import MachConstMachineForm from './MachConstMachineForm'; // Import the form component
import Button from '../../../components/common/Button';

const { Title } = Typography;

const formatMachineEnum = (value?: string | null) => {
  const map: Record<string, string> = {
    Available: 'Available',
    Assigned: 'Assigned',
    InMaintenance: 'In Maintenance',
    OutOfService: 'Out of Service',
    Inactive: 'Inactive',
    CompanyOwned: 'Company Owned',
    Rented: 'Rented',
    Leased: 'Leased',
    owned: 'Company Owned',
    rental: 'Rented',
  };
  return map[value || ''] || value || '-';
};

const includesText = (value: unknown, query: string) =>
  String(value ?? '').toLowerCase().includes(query.toLowerCase());

const toProperCase = (value: string): string =>
  value
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

const MachConstMachinesList: React.FC = () => {
  const translateMachineEnum = (value?: string | null) => formatMachineEnum(value);
  const navigate = useNavigate();
  const queryClient = useQueryClient(); // Get query client instance
  // Fetch data using the new hook that gets data from the view
  const { data: machinesViewData, isLoading, error } = useGetVwConstMachines();

  const machines: VwConstMachines[] = Array.isArray(machinesViewData)
    ? machinesViewData
    : (machinesViewData?.data || []);

  // --- Fetch Lookup Data for Form ---
  const { data: companiesData, isLoading: isLoadingCompanies } = useGetCompanies();
  const { data: personnelData, isLoading: isLoadingPersonnel } = useGetPersonnel();
  const companies = companiesData?.data || [];
  const personnel = personnelData?.data || [];
  const isLoadingLookups = isLoadingCompanies || isLoadingPersonnel;
  // State for filters
  const [filters, setFilters] = useState({
    license_plate: '',
    machine_type: '',
    make: '',
    model: '',
    ownership_type: '', // Keep ownership filter state
    company_name: '', // Filter by company name from the view
    operator_name: '', // Filter by operator name from the view
    status: '',
    contract_reference_id: '',
  });

  // --- State for Form Drawer ---
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingMachine, setEditingMachine] = useState<ConstMachines | null>(null);
  const [typeModalOpen, setTypeModalOpen] = useState(false);
  const [newMachineType, setNewMachineType] = useState('');
  const [addingMachineType, setAddingMachineType] = useState(false);
  const [machineTypeOptions, setMachineTypeOptions] = useState<{ value: string; label: string }[]>([]);

  const handleMachineTypesChange = useCallback((types: string[]) => {
    setMachineTypeOptions(
      [...types]
        .map((value) => value.trim())
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b))
        .map((value) => ({ value, label: value }))
    );
  }, []);

  // Handle filter changes
  const handleFilterChange = (name: string, value: string | null | undefined) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      [name]: value ?? '',
    }));
  };

  // --- Delete Mutation ---
  const deleteMachineMutation = useMutation<unknown, Error, number>({
    mutationFn: (id: number) => deleteData('const_machines', 'id', id), // Assuming table name is 'const_machines' and PK is 'id'
    onSuccess: (_, id) => {
      message.success('Machine deleted (ID: {{id}})');
      queryClient.invalidateQueries({ queryKey: ['vw_const_machines'] }); // Invalidate the view query
    },
    onError: (error: any, id) => {
      console.error(`Error deleting machine (ID: ${id}):`, error);
      const errorMsg = error.response?.data?.error || error.message || 'Unknown error';
      message.error(errorMsg);
    },
  });

  // --- Add Mutation ---
  const addMachineMutation = useMutation<ConstMachines, Error, Partial<ConstMachines>>({
    mutationFn: (newData) => insertData('const_machines', newData),
    onSuccess: (data) => {
      message.success(`Machine added: "${data.machine_type}"`);
      queryClient.invalidateQueries({ queryKey: ['vw_const_machines'] });
      handleCloseForm();
    },
    onError: (error: any) => {
      console.error("Error adding machine:", error);
      const errorMsg = error.response?.data?.error || error.message || 'Unknown error';
      message.error(errorMsg);
    },
  });

  // --- Update Mutation ---
  const updateMachineMutation = useMutation<ConstMachines, Error, { id: number; data: Partial<ConstMachines> }>({
    mutationFn: ({ id, data }) => updateData('const_machines', id, data),
    onSuccess: (data) => {
      message.success(`Machine updated: "${data.machine_type}"`);
      queryClient.invalidateQueries({ queryKey: ['vw_const_machines'] });
      handleCloseForm();
    },
    onError: (error: any, variables) => {
      console.error(`Error updating machine (ID: ${variables.id}):`, error);
      const errorMsg = error.response?.data?.error || error.message || 'Unknown error';
      message.error(errorMsg);
    },
  });

  // --- Event Handlers ---
  const handleDelete = (id: number) => {
    deleteMachineMutation.mutate(id);
  };

  const handleAdd = () => {
    setEditingMachine(null); // Ensure no initial values for add
    setIsFormOpen(true);
  };

  const handleAddMachineType = async () => {
    const label = toProperCase(newMachineType.trim());
    if (!label) {
      message.warning('Enter a machine type');
      return;
    }
    const alreadyExists = machineTypeOptions.some(
      (option) => option.value.toLocaleLowerCase() === label.toLocaleLowerCase()
    );
    if (alreadyExists) {
      message.error('That machine type already exists');
      return;
    }
    setNewMachineType(label);
    setAddingMachineType(true);
    try {
      await alterEnumTypes('machine_type_enum', label);
      message.success('Machine type added');
      setNewMachineType('');
      setTypeModalOpen(false);
      await queryClient.invalidateQueries({ queryKey: ['enum', 'machine_type_enum'] });
    } catch (error: any) {
      const code = error?.response?.data?.error;
      if (code === 'exists') message.error('That machine type already exists');
      else if (code === 'invalid') message.error('That machine type name is not allowed');
      else message.error('Could not add the machine type');
    } finally {
      setAddingMachineType(false);
    }
  };

  const handleEdit = (record: VwConstMachines) => {
    // Convert VwConstMachines to ConstMachines if necessary, or ensure types match
    // For now, assuming VwConstMachines has all fields needed by ConstMachines form
    setEditingMachine(record as ConstMachines); // Cast might be needed if types differ significantly
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditingMachine(null);
  };

  // This function is passed to the Form component's onSubmit prop
  const handleSubmit = (values: Partial<ConstMachines>) => {
    // Basic data cleaning/formatting
    const processedValues: Partial<ConstMachines> = {
      ...values,
      year: values.year ? Number(values.year) : null, // Ensure year is number or null
      hours_counter: values.hours_counter ? Number(values.hours_counter) : 0, // Ensure hours is number or 0
      operator_id: values.operator_id ? Number(values.operator_id) : null,
      contract_reference_id: values.contract_reference_id?.trim() || null, // Ensure operator_id is number or null
      // specs alanı zaten form içinde normalleştirildi; burada sadece null/undefined bırakıyoruz
      specs: values.specs ?? null,
    };

    if (editingMachine?.id) {
      updateMachineMutation.mutate({ id: editingMachine.id, data: processedValues });
    } else {
      addMachineMutation.mutate(processedValues);
    }
  };

  // Define table columns based on the VwConstMachines interface
  const columns = [
    {
      title: 'Type',
      dataIndex: 'machine_type',
      key: 'machine_type',
      sorter: (a: VwConstMachines, b: VwConstMachines) => (a.machine_type || '').localeCompare(b.machine_type || ''),
    },
    {
      title: 'Plate / Serial No',
      dataIndex: 'license_plate',
      key: 'license_plate',
      // Removed link render, actions column handles navigation
    },
    {
      title: 'Make',
      dataIndex: 'make',
      key: 'make',
    },
    {
      title: 'Model',
      dataIndex: 'model',
      key: 'model',
    },
    {
      title: 'Year',
      dataIndex: 'year',
      key: 'year',
      width: 70,
      render: (value?: number | null) => value || '-',
      sorter: (a: VwConstMachines, b: VwConstMachines) => (a.year || 0) - (b.year || 0),
    },
    {
      title: 'Ownership',
      dataIndex: 'ownership_type',
      key: 'ownership_type',
      render: (value: string) => translateMachineEnum(value),
      sorter: (a: VwConstMachines, b: VwConstMachines) => (a.ownership_type || '').localeCompare(b.ownership_type || ''),
    },
    {
      title: 'Company',
      dataIndex: 'company_name', // Use company_name from the view
      key: 'company_name',
      // No render needed, directly display company_name
      sorter: (a: VwConstMachines, b: VwConstMachines) => (a.company_name || '').localeCompare(b.company_name || ''),
    },
    {
      title: 'Operator',
      dataIndex: 'operator_name', // Use operator_name from the view
      key: 'operator_name',
      sorter: (a: VwConstMachines, b: VwConstMachines) => (a.operator_name || '').localeCompare(b.operator_name || ''),
    },
    {
      title: 'Hours',
      dataIndex: 'hours_counter',
      key: 'hours_counter',
      sorter: (a: VwConstMachines, b: VwConstMachines) => (a.hours_counter || 0) - (b.hours_counter || 0),
    },
    {
      title: 'Arrival Date',
      dataIndex: 'arrival_date',
      key: 'arrival_date',
      render: (value: string) => (value ? dayjs(value).format('DD/MM/YYYY') : '-'),
      sorter: (a: VwConstMachines, b: VwConstMachines) => (a.arrival_date || '').localeCompare(b.arrival_date || ''),
    },
    {
      title: 'Departure Date',
      dataIndex: 'departure_date',
      key: 'departure_date',
      render: (value: string) => (value ? dayjs(value).format('DD/MM/YYYY') : '-'),
      sorter: (a: VwConstMachines, b: VwConstMachines) => (a.departure_date || '').localeCompare(b.departure_date || ''),
    },
    {
      title: 'Contract',
      dataIndex: 'contract_reference_id',
      key: 'contract_reference_id',
      render: (value?: string | null) => value || '-',
      sorter: (a: VwConstMachines, b: VwConstMachines) => (a.contract_reference_id || '').localeCompare(b.contract_reference_id || ''),
    },
    {
      title: 'Specifications',
      dataIndex: 'specs',
      key: 'specs',
      render: (specs: Record<string, any> | null | undefined) => {
        if (!specs || typeof specs !== 'object') return null;
        const entries = Object.entries(specs).filter(([k, v]) => k && v != null && String(v).trim() !== '');
        if (!entries.length) return null;
        // Örnek çıktı: "Power: 150 kW; Capacity: 30 t"
        return entries
          .map(([k, v]) => `${k}: ${String(v)}`)
          .join('; ');
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        let color = 'default';
        if (status === 'Available') color = 'success';
        if (status === 'Assigned') color = 'processing';
        if (status === 'InMaintenance') color = 'warning';
        if (status === 'OutOfService') color = 'error';
        if (status === 'Inactive') color = 'default';
        return <Tag color={color}>{translateMachineEnum(status)}</Tag>;
      },
      sorter: (a: VwConstMachines, b: VwConstMachines) => (a.status || '').localeCompare(b.status || ''),
    },
    createActionColumn<VwConstMachines>({
      onView: (record) => record.id && navigate(`/machinery/machines/show/${record.id}`),
      onEdit: (record) => handleEdit(record),
      onDelete: (record) => {
        const numericId = record.id ? parseInt(String(record.id), 10) : NaN;
        if (isNaN(numericId)) return;
        Modal.confirm({
          title: 'Are you sure you want to delete this machine?',
          okText: 'Yes',
          cancelText: 'No',
          okButtonProps: { danger: true },
          onOk: () => handleDelete(numericId),
        });
      },
      align: 'left',
    }) as any,
  ];

  type MachineFilterKey = keyof typeof filters;

  const machineMatches = useCallback((machine: VwConstMachines, except?: MachineFilterKey) => {
    if (!machine) return false;
    if (except !== 'license_plate' && filters.license_plate && !includesText(machine.license_plate, filters.license_plate)) return false;
    if (except !== 'machine_type' && filters.machine_type && !includesText(machine.machine_type, filters.machine_type)) return false;
    if (except !== 'make' && filters.make && !includesText(machine.make, filters.make)) return false;
    if (except !== 'model' && filters.model && !includesText(machine.model, filters.model)) return false;
    if (except !== 'ownership_type' && filters.ownership_type && machine.ownership_type !== filters.ownership_type) return false;
    if (except !== 'company_name' && filters.company_name && !includesText(machine.company_name, filters.company_name)) return false;
    if (except !== 'operator_name' && filters.operator_name && !includesText(machine.operator_name, filters.operator_name)) return false;
    if (except !== 'status' && filters.status && machine.status !== filters.status) return false;
    if (except !== 'contract_reference_id' && filters.contract_reference_id && machine.contract_reference_id !== filters.contract_reference_id) return false;
    return true;
  }, [filters]);

  const filteredMachines = useMemo(
    () => machines.filter((machine) => machineMatches(machine)),
    [machines, machineMatches]
  );

  const distinctSorted = (values: Array<string | null | undefined>) =>
    Array.from(new Set(values.map((value) => String(value ?? '').trim()).filter(Boolean)))
      .sort((a, b) => a.localeCompare(b));

  const optionsFor = useCallback((field: MachineFilterKey) => {
    const pool = machines.filter((machine) => machineMatches(machine, field));
    const values = pool.map((machine) => {
      if (field === 'license_plate') return machine.license_plate;
      if (field === 'machine_type') return machine.machine_type;
      if (field === 'make') return machine.make;
      if (field === 'model') return machine.model;
      if (field === 'ownership_type') return machine.ownership_type;
      if (field === 'company_name') return machine.company_name;
      if (field === 'operator_name') return machine.operator_name;
      if (field === 'status') return machine.status;
      return machine.contract_reference_id;
    });
    return distinctSorted(values).map((value) => ({
      value,
      label: field === 'ownership_type' || field === 'status' ? translateMachineEnum(value) : value,
    }));
  }, [machines, machineMatches]);
  if (error) {
    message.error(`Data could not be loaded: ${error.message}`);
    // Optionally return an error component or message
  }

  return (
    <Card
      title={
        <Title level={4} style={{ margin: 0 }}>
          <ToolOutlined style={{ marginRight: 8 }} />
          {'Machine Fleet'}
        </Title>
      }
      bordered={false}
      extra={
        <Space>
          <Button action="add" onClick={() => setTypeModalOpen(true)}>{'Add Machine Type'}</Button>
          <Button action="add" type="primary" icon={<PlusOutlined />} onClick={handleAdd}>{'Add Machine'}</Button>
        </Space>
      }
    >
      <FilterBar
        items={[
          { type: 'autocomplete', key: 'license_plate', placeholder: 'Plate / Serial No', value: filters.license_plate, onChange: v => handleFilterChange('license_plate', v), options: optionsFor('license_plate'), width: 200, allowClear: true },
          { type: 'select', key: 'machine_type', placeholder: 'Machine type', value: filters.machine_type || undefined, onChange: v => handleFilterChange('machine_type', v), options: optionsFor('machine_type'), width: 180, allowClear: true, showSearch: true },
          { type: 'select', key: 'make', placeholder: 'Make', value: filters.make || undefined, onChange: v => handleFilterChange('make', v), options: optionsFor('make'), width: 160, allowClear: true, showSearch: true },
          { type: 'select', key: 'model', placeholder: 'Model', value: filters.model || undefined, onChange: v => handleFilterChange('model', v), options: optionsFor('model'), width: 160, allowClear: true, showSearch: true },
          { type: 'select', key: 'ownership_type', placeholder: 'Ownership', value: filters.ownership_type || undefined, onChange: v => handleFilterChange('ownership_type', v), options: optionsFor('ownership_type'), width: 140, allowClear: true },
          { type: 'select', key: 'company_name', placeholder: 'Company', value: filters.company_name || undefined, onChange: v => handleFilterChange('company_name', v), options: optionsFor('company_name'), width: 200, allowClear: true, showSearch: true },
          { type: 'select', key: 'operator_name', placeholder: 'Operator', value: filters.operator_name || undefined, onChange: v => handleFilterChange('operator_name', v), options: optionsFor('operator_name'), width: 200, allowClear: true, showSearch: true },
          { type: 'select', key: 'contract_reference_id', placeholder: 'Contract Reference', value: filters.contract_reference_id || undefined, onChange: v => handleFilterChange('contract_reference_id', v), options: optionsFor('contract_reference_id'), width: 220, allowClear: true, showSearch: true },
          { type: 'select', key: 'status', placeholder: 'Status', value: filters.status || undefined, onChange: v => handleFilterChange('status', v), options: optionsFor('status'), width: 140, allowClear: true },
        ]}
      />
      <StandardTable
        columns={columns as any}
        dataSource={filteredMachines}
        rowKey="id"
        size="small"
        scroll={{ x: 'max-content' }}
        pagination={{ pageSize: 20, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
        loading={isLoading || deleteMachineMutation.isPending}
        bordered
      />
      <Modal
        title={'Add machine type'}
        open={typeModalOpen}
        onCancel={() => {
          if (addingMachineType) return;
          setTypeModalOpen(false);
          setNewMachineType('');
        }}
        onOk={handleAddMachineType}
        confirmLoading={addingMachineType}
        okText={'Add Machine Type'}
        destroyOnClose
      >
        <AutoComplete
          style={{ width: '100%' }}
          options={machineTypeOptions}
          value={newMachineType}
          onChange={setNewMachineType}
          placeholder={'New type name'}
          autoFocus
          allowClear
          filterOption={(input, option) =>
            String(option?.value ?? '').toLocaleLowerCase().includes(input.trim().toLocaleLowerCase())
          }
        >
          <Input maxLength={63} onPressEnter={handleAddMachineType} />
        </AutoComplete>
      </Modal>
      {/* Render the Form Drawer (Imported Component) */}
      <MachConstMachineForm
        open={isFormOpen}
        onClose={handleCloseForm}
        onSubmit={handleSubmit}
        initialValues={editingMachine}
        companies={companies}
        personnel={personnel}
        isLoadingLookups={isLoadingLookups || addMachineMutation.isPending || updateMachineMutation.isPending} // Pass combined loading state
        onMachineTypesChange={handleMachineTypesChange}
      />
    </Card>
  );
};

export default MachConstMachinesList;
